import { useEffect, useRef, useState, FormEvent, FC, ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { fetchAllCountries, Country, verifyRecaptcha } from "../services/api";
import { PageTitle } from "./PageTitle";
import { HowToUse } from "./HowToUse";
import { Toast } from "./Toast";
import { useToast } from "../hooks/useToast";

export const Home: FC = () => {
  const [desc, setDesc] = useState<string>("");
  const [country, setCountry] = useState<Country | null>(null);
  const [query, setQuery] = useState<string>("");
  const [countries, setCountries] = useState<Country[]>([]);
  const [filtered, setFiltered] = useState<Country[]>([]);
  const [showDropdown, setShowDropdown] = useState<boolean>(false);

  // reCAPTCHA state
  const [isVerified, setIsVerified] = useState<boolean>(false);
  const [isVerifying, setIsVerifying] = useState<boolean>(false);

  const navigate = useNavigate();
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY!;

  // Toast notifications
  const { toasts, hideToast, showSuccess, showError, showWarning } = useToast();

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const data = await fetchAllCountries();
        setCountries(data);
      } catch (err) {
        console.error("Failed to fetch countries", err);
        showError("Failed to load countries. Please refresh the page.");
      }
    };
    loadCountries();
  }, [showError]);

  // Handle reCAPTCHA change - call backend immediately (GPSR pattern)
  const handleRecaptchaChange = async (token: string | null) => {
    if (!token) {
      setIsVerified(false);
      return;
    }

    setIsVerifying(true);

    try {
      // Call backend to verify the token
      const response = await verifyRecaptcha(token);
      
      // Store JWT token in localStorage for authenticated requests
      localStorage.setItem('authToken', response.token);
      
      setIsVerified(true);
      showSuccess('reCAPTCHA verified successfully!');
      console.log('reCAPTCHA verified successfully');
    } catch (error) {
      console.error('Failed to verify reCAPTCHA:', error);
      
      let errorMessage = 'Failed to verify reCAPTCHA. Please try again.';
      
      if (error instanceof Error) {
        if (error.message.includes('timeout') || error.message.includes('not responding')) {
          errorMessage = 'Backend server is not responding. Please make sure the backend is running.';
        } else if (error.message.includes('Failed to verify')) {
          errorMessage = 'reCAPTCHA verification failed. Please try again.';
        }
      }
      
      showError(errorMessage);
      setIsVerified(false);
      
      // Reset reCAPTCHA widget on error
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const onCountryChange = (value: string) => {
    setQuery(value);
    if (value.trim() === "") {
      setFiltered([]);
      setShowDropdown(false);
    } else {
      const matches = countries.filter((c) =>
        c.name.toLowerCase().includes(value.toLowerCase())
      );
      setFiltered(matches);
      setShowDropdown(true);
    }
  };

  const selectCountry = (countryObj: Country) => {
    setCountry(countryObj);
    setQuery(countryObj.name);
    setFiltered([]);
    setShowDropdown(false);
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!country) {
      showWarning('Please select a country of origin.');
      return;
    }

    // Require verification
    if (!isVerified) {
      showWarning('Please complete the reCAPTCHA verification.');
      return;
    }

    navigate("/results", { state: { desc, country } });
  };

  return (
    <div className="min-h-screen hero-bg flex items-start">
      {/* Toast Notifications */}
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => hideToast(toast.id)}
        />
      ))}

      <div className="w-[92vw] max-w-[1100px] mx-auto my-12 relative">
        <PageTitle />
        <HowToUse />

        <form
          onSubmit={onSubmit}
          className="bg-white border border-neutral-200 rounded-3xl shadow-soft p-7 grid gap-4"
        >
          <textarea
            id="desc"
            placeholder='Describe your product, like “Men’s cotton t-shirt” Or provide customs code, like 6109.10'
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            rows={5}
            className="w-full rounded-2xl bg-neutral-100 border border-neutral-300
                        focus:bg-white focus:border-blue-300 focus:ring-4 focus:ring-blue-200/50
                        outline-none p-5 text-lg
                        placeholder:text-left"
            required
          />


          <div className="relative">
            <input
              id="country"
              placeholder="Country of Origin"
              value={query}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onCountryChange(e.target.value)
              }
              className="w-full rounded-xl bg-neutral-100 border border-neutral-300 focus:bg-white focus:border-blue-300 focus:ring-4 focus:ring-blue-200/50 outline-none p-4 text-lg"
              required
              autoComplete="off"
            />

            {showDropdown && filtered.length > 0 && (
              <ul className="absolute z-10 bg-white border border-neutral-200 rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto w-full">
                {filtered.map((c) => (
                  <li
                    key={c.id}
                    onClick={() => selectCountry(c)}
                    className="px-4 py-2 hover:bg-neutral-100 cursor-pointer"
                  >
                    {c.name}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="mt-3 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            {/* Left: reCAPTCHA */}
            <div className="w-fit">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={SITE_KEY}
                onChange={handleRecaptchaChange}
                onExpired={() => {
                  setIsVerified(false);
                  showWarning('reCAPTCHA expired. Please verify again.');
                  if (recaptchaRef.current) {
                    recaptchaRef.current.reset();
                  }
                }}
                onErrored={() => {
                  setIsVerified(false);
                  showError('reCAPTCHA error occurred. Please try again.');
                  if (recaptchaRef.current) {
                    recaptchaRef.current.reset();
                  }
                }}
              />
              
            </div>

            {/* Right: button */}
            <button
              type="submit"
              disabled={!isVerified || isVerifying}
              className="inline-flex items-center gap-2 bg-black text-white rounded-full px-5 py-3 font-semibold shadow-lg hover:bg-neutral-900 active:shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>See EU tariff rates</span>
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" className="mt-[1px]">
                <path
                  d="M5 12h14M13 5l7 7-7 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
